/**
 * ExportButton - Reusable export to CSV button component
 * Only shows if user has data_export RBAC permission
 */
import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { exportAPI } from '../lib/api';

export default function ExportButton({ 
  exportFn, 
  filename, 
  label = 'Export', 
  variant = 'outline',
  size = 'sm',
  className = ''
}) {
  const [exporting, setExporting] = useState(false);
  const [hasPermission, setHasPermission] = useState(null);

  useEffect(() => {
    checkPermission();
  }, []);

  const checkPermission = async () => {
    try {
      const res = await exportAPI.checkPermission();
      setHasPermission(res.data.has_export_permission);
    } catch (error) {
      // If check fails, hide the button
      setHasPermission(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await exportFn();
      
      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Export downloaded successfully');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('You don\'t have permission to export data');
      } else {
        toast.error(error.response?.data?.detail || 'Export failed');
      }
    } finally {
      setExporting(false);
    }
  };

  // Don't render if no permission or still checking
  if (hasPermission === null || hasPermission === false) {
    return null;
  }

  return (
    <Button 
      variant={variant} 
      size={size} 
      onClick={handleExport} 
      disabled={exporting}
      className={className}
      data-testid="export-btn"
    >
      {exporting ? (
        <Loader2 className="w-4 h-4 animate-spin mr-2" />
      ) : (
        <Download className="w-4 h-4 mr-2" />
      )}
      {label}
    </Button>
  );
}
